import { useCallback } from 'react';
import { trackEvent } from '@/lib/analytics';

/**
 * Floor plan PDF mapping based on configuration type.
 * Each configuration maps to the appropriate floor plan PDF.
 * Includes both A&B Wing and C&D Wing plans.
 */
const floorPlanMapping: Record<string, string> = {
  // 2 BHK configurations (A&B Wing)
  '2 BHK': '/floor-plans/2bhk-flat-03.pdf',
  '2bhk': '/floor-plans/2bhk-flat-03.pdf',
  
  // 3 BHK Small (~1,006-1,069 sq.ft) - C Wing
  '3 BHK (S)': '/floor-plans/cd-3bhk-s-flat-03.pdf',
  '3bhk': '/floor-plans/cd-3bhk-s-flat-03.pdf',
  '3 BHK': '/floor-plans/cd-3bhk-s-flat-03.pdf',
  
  // 3 BHK Medium M1 (~1,320-1,537 sq.ft) - A&B Wing and C&D Wing
  '3 BHK (M1)': '/floor-plans/cd-3bhk-m1-flat-01.pdf',
  '3 BHK (M)': '/floor-plans/cd-3bhk-m1-flat-01.pdf',
  '3bhk-m1': '/floor-plans/cd-3bhk-m1-flat-01.pdf',
  '3bhk-1424': '/floor-plans/cd-3bhk-m1-flat-01.pdf',
  
  // 3 BHK Medium M2 (~1,258 sq.ft) - D Wing
  '3 BHK (M2)': '/floor-plans/cd-3bhk-m2-flat-02.pdf',
  '3bhk-m2': '/floor-plans/cd-3bhk-m2-flat-02.pdf',
  
  // 3 BHK Large (~1,537-1,564 sq.ft) - D Wing
  '3 BHK (L)': '/floor-plans/cd-3bhk-l-flat-03.pdf',
  '3bhk-large': '/floor-plans/cd-3bhk-l-flat-03.pdf',
  '3 BHK Large': '/floor-plans/cd-3bhk-l-flat-03.pdf',
  
  // 3 BHK XL (~1,864 sq.ft) - D Wing 3BHK L configuration
  '3 BHK (XL)': '/floor-plans/cd-3bhk-l-flat-03.pdf',
  '3bhk-1864': '/floor-plans/cd-3bhk-l-flat-03.pdf',
  
  // 4 BHK (~2,178-2,487 sq.ft) - C&D Wing
  '4 BHK': '/floor-plans/cd-4bhk-flat-01.pdf',
  '4bhk': '/floor-plans/cd-4bhk-flat-01.pdf',
};

/**
 * Get all available floor plan PDFs for downloading all plans
 */
const allFloorPlans = [
  // Master Layout
  { name: 'Master Layout Plan', path: '/floor-plans/master-layout.pdf' },
  
  // A&B Wing Plans
  { name: 'A&B Wing Typical Floor', path: '/floor-plans/ab-wing-typical-floor.pdf' },
  { name: '3 BHK M1 - Flat 01 (A&B Wing)', path: '/floor-plans/3bhk-m1-flat-01.pdf' },
  { name: '3 BHK M1 - Flat 02 (A&B Wing)', path: '/floor-plans/3bhk-m1-flat-02.pdf' },
  { name: '3 BHK S - Flat 03 (A&B Wing)', path: '/floor-plans/3bhk-s-flat-03.pdf' },
  { name: '3 BHK S - Flat 04 (A&B Wing)', path: '/floor-plans/3bhk-s-flat-04.pdf' },
  { name: '2 BHK - Flat 03 (A&B Wing)', path: '/floor-plans/2bhk-flat-03.pdf' },
  { name: '2 BHK - Flat 04 (A&B Wing)', path: '/floor-plans/2bhk-flat-04.pdf' },
  { name: 'A Wing Refuge Floor', path: '/floor-plans/a-wing-refuge-floor.pdf' },
  { name: 'B Wing Refuge Floor', path: '/floor-plans/b-wing-refuge-floor.pdf' },
  
  // C&D Wing Plans
  { name: 'C&D Wing Typical Floor', path: '/floor-plans/cd-wing-typical-floor.pdf' },
  { name: 'D Wing Typical Floor', path: '/floor-plans/d-wing-typical-floor.pdf' },
  { name: '3 BHK M1 - Flat 01 (C&D Wing)', path: '/floor-plans/cd-3bhk-m1-flat-01.pdf' },
  { name: '3 BHK M1 - Flat 02 (C&D Wing)', path: '/floor-plans/cd-3bhk-m1-flat-02.pdf' },
  { name: '3 BHK S - Flat 03 (C Wing)', path: '/floor-plans/cd-3bhk-s-flat-03.pdf' },
  { name: '3 BHK S - Flat 04 (C Wing)', path: '/floor-plans/cd-3bhk-s-flat-04.pdf' },
  { name: '3 BHK M2 - Flat 02 (D Wing)', path: '/floor-plans/cd-3bhk-m2-flat-02.pdf' },
  { name: '4 BHK - Flat 01 (D Wing)', path: '/floor-plans/cd-4bhk-flat-01.pdf' },
  { name: '3 BHK L - Flat 03 (D Wing)', path: '/floor-plans/cd-3bhk-l-flat-03.pdf' },
  { name: 'C&D Wing Refuge Floor', path: '/floor-plans/cd-wing-refuge-floor.pdf' },
  { name: 'C&D Wing Refuge Floor 2', path: '/floor-plans/cd-wing-refuge-floor-2.pdf' },
  { name: 'C&D Wing Refuge Floor 3', path: '/floor-plans/cd-wing-refuge-floor-3.pdf' },
  { name: 'C&D Wing Refuge Floor 4', path: '/floor-plans/cd-wing-refuge-floor-4.pdf' },
];

/**
 * Hook for downloading floor plan PDFs based on configuration.
 * Auto-downloads the appropriate PDF when called.
 */
export const useFloorPlanDownload = () => {
  /**
   * Download a specific floor plan based on configuration name
   */
  const downloadFloorPlan = useCallback((configName: string) => {
    const pdfPath = floorPlanMapping[configName] || '/floor-plans/master-layout.pdf';
    
    trackEvent('floor_plan_download', { 
      configuration: configName,
      pdf_path: pdfPath 
    });
    
    // Create a temporary anchor to trigger download
    const link = document.createElement('a');
    link.href = pdfPath;
    link.download = `nahar-chandi-valley-floor-plan-${configName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  /**
   * Download the master layout plan
   */
  const downloadMasterLayout = useCallback(() => {
    trackEvent('master_layout_download', {});
    
    const link = document.createElement('a');
    link.href = '/floor-plans/master-layout.pdf';
    link.download = 'nahar-chandi-valley-master-layout.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  /**
   * Download all floor plans (comprehensive typical floor plan)
   */
  const downloadAllFloorPlans = useCallback(() => {
    trackEvent('floor_plan_download_all', {});
    
    // Download the master layout as the comprehensive plan
    const link = document.createElement('a');
    link.href = '/floor-plans/master-layout.pdf';
    link.download = 'nahar-chandi-valley-all-floor-plans.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  /**
   * Get the PDF path for a configuration (for direct linking)
   */
  const getFloorPlanPath = useCallback((configName: string): string => {
    return floorPlanMapping[configName] || '/floor-plans/master-layout.pdf';
  }, []);

  return {
    downloadFloorPlan,
    downloadMasterLayout,
    downloadAllFloorPlans,
    getFloorPlanPath,
    allFloorPlans,
  };
};
